import { useEffect, useState, useRef, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonMenuButton,
  IonBackButton,
  IonTitle,
  IonContent,
  IonList,
  IonSearchbar,
  IonButton,
  IonIcon,
  IonRefresher,
  IonRefresherContent,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  useIonViewDidEnter,
  useIonViewDidLeave,
  useIonViewWillLeave
} from '@ionic/react';
import { filter } from 'ionicons/icons';
import { toJS } from 'mobx';

import CategoryFilterModal from './components/CategoryFilterModal';
import TypeFilters from './components/TypeFilters';
import ErListItem from './components/ErListItem';
import ErListItemLoader from './components/ErListItem/ErListItemLoader';

import layoutStore from 'stores/layout.store';
import erCategoryStore from 'stores/er-category.store';
import erTagStore from 'stores/er-tag.store';
import educationalResourceStore from 'stores/educational-resource.store';
import apolloClient from 'libs/apollo/apollo.lib';

import classes from './style.module.scss';

const ER_CATEGORY_FIELDS = `
  id
  name 
`;

const ER_TAG_FIELDS = `
  id
  name 
`;

const ER_EDUCATIONAL_RESOURCES_FIELDS = `
    id
    name
    description
    thumbnail
    type
    article
    image
    video_url
    file
    er_categories {
      id
      name
    }
    er_tags {
      id
      name
    }
    createdBy {
      __typename
      ... on BarwisAdmin {
        id
        user {
          id
          first_name
          last_name
        }
      }
    }
    created_at
    updated_at
`;

const EducationalResourcePage = ({ match, history, location }) => {
  const [searchText, setSearchText] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isInfiniteScrollLoading, setIsInfiniteScrollLoading] = useState(false);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [erResources, setErResources] = useState([]);
  const [isInitLoaded, setIsInitLoaded] = useState(false);
  const ionInfiniteScroll = useRef();
  const selectedType = new URLSearchParams(location.search).get('type');
  const selectedTagTitle = new URLSearchParams(location.search).get('title');
  const educationalResources = educationalResourceStore.getObjects();
  const isErCategoriesLoading = erCategoryStore.getIsLoading();
  const isErTagsLoading = erTagStore.getIsLoading();
  const isEducationalResourcesLoading = educationalResourceStore.getIsLoading();
  const isLoading =
    isErCategoriesLoading || isErTagsLoading || isEducationalResourcesLoading;

  const fetchErCategories = async () => {
    try {
      await erCategoryStore.searchObjectsAll();
    } catch (error) {
      console.log(error);
    }
  };

  const fetchEducationalResources = async () => {
    try {
      await educationalResourceStore.searchObjects();
    } catch (error) {
      console.log(error);
    }
  };

  useIonViewDidEnter(() => {
    const initLoad = async () => {
      erCategoryStore.setFields(ER_CATEGORY_FIELDS);
      educationalResourceStore.setFields(ER_EDUCATIONAL_RESOURCES_FIELDS);
      erCategoryStore.setQuery({});
      educationalResourceStore.setObjects([]);
      setErResources([]);
      await fetchErCategories();
      buildQuery();
      await fetchEducationalResources();
      setIsInitLoaded(true);
    };
    initLoad();
  });

  useIonViewWillLeave(() => {
    educationalResourceStore.setObjects([]);
    setErResources([]);
    setIsInitLoaded(false);
  });

  useEffect(() => {
    if (educationalResources.length > 0) {
      setErResources((prevErResources) => [
        ...new Map(
          [...prevErResources, ...toJS(educationalResources)].map(
            (erResource) => [erResource['id'], erResource]
          )
        ).values()
      ]);
    }
  }, [educationalResources]);

  const buildQuery = useCallback(() => {
    let query = [];
    if (selectedCategoryIds.length > 0) {
      query = [
        ...query,
        {
          HAS: {
            relation: 'er_categories',
            condition: {
              column: 'id',
              operator: 'IN',
              value: selectedCategoryIds
            }
          }
        }
      ];
    }

    if (selectedType && selectedType !== 'all') {
      query = [
        ...query,
        {
          column: 'type',
          operator: 'EQ',
          value: selectedType
        }
      ];
    }

    if (searchText) {
      query = [
        ...query,
        {
          column: 'name',
          operator: 'LIKE',
          value: `%${searchText}%`
        }
      ];
    }
    educationalResourceStore.setQuery({
      AND: query
    });
  }, [selectedCategoryIds, selectedType, searchText]);

  useEffect(() => {
    if (!isInitLoaded) return;
    buildQuery();
    educationalResourceStore.setObjects([]);
    setErResources([]);
    educationalResourceStore.setPage(1);
    educationalResourceStore.abortAllPendingRequests();
    fetchEducationalResources();
  }, [buildQuery, isInitLoaded]);

  const _renderContent = () => {
    return (
      <>
        <IonRefresher
          onIonRefresh={async (e) => {
            setIsRefreshing(true);
            educationalResourceStore.abortAllPendingRequests();
            await apolloClient.clearStore();
            educationalResourceStore.setPage(1);
            setErResources([]);
            await fetchEducationalResources();
            e.detail.complete();
            setIsRefreshing(false);
          }}
          slot='fixed'
        >
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>

        <IonList className={classes.ionList} lines='none'>
          {(!isLoading || !isRefreshing) &&
            erResources.map((erResource, index) => (
              <ErListItem
                erResource={erResource}
                key={erResource.id}
                push={history.push}
              />
            ))}
        </IonList>
        {erResources.length > 0 && (
          <IonInfiniteScroll
            ref={ionInfiniteScroll}
            threshold='100px'
            onIonInfinite={async (e) => {
              setIsInfiniteScrollLoading(true);
              const currentPage = educationalResourceStore.getPage();
              const lastPage =
                educationalResourceStore.getPaginationInfo().lastPage;
              if (currentPage < lastPage) {
                const nextPage = currentPage + 1;
                educationalResourceStore.setPage(nextPage);
                educationalResourceStore.abortAllPendingRequests();
                await fetchEducationalResources();
              }
              await ionInfiniteScroll.current.complete();
              setIsInfiniteScrollLoading(false);
            }}
          >
            <IonInfiniteScrollContent
              loadingSpinner='dots'
              loadingText='Loading more...'
            ></IonInfiniteScrollContent>
          </IonInfiniteScroll>
        )}

        {/* <IonLoading isOpen={isLoading} /> */}
        <CategoryFilterModal
          isOpen={showFilters}
          close={() => setShowFilters(false)}
          selectedCatIds={selectedCategoryIds}
          apply={(selectedCatIds) => {
            setSelectedCategoryIds(selectedCatIds);
            setShowFilters(false);
          }}
        />
      </>
    );
  };

  const _renderLoader = () => {
    return (
      <IonList className={classes.ionList} lines='none'>
        <ErListItemLoader />
        <ErListItemLoader />
        <ErListItemLoader />
        <ErListItemLoader />
      </IonList>
    );
  };

  return (
    <IonPage id='educational-resources'>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot='start'>
            <IonMenuButton></IonMenuButton>
          </IonButtons>
          <IonTitle>Educational Resources</IonTitle>
        </IonToolbar>
        <IonToolbar className={classes.searchToolbar}>
          <IonSearchbar
            placeholder='Search Educational Resources'
            showClearButton='never'
            className={classes.ionSearchbar}
            value={searchText}
            onIonChange={(e) => {
              educationalResourceStore.setPage(1);
              setSearchText(e.detail.value);
            }}
            debounce={500}
          ></IonSearchbar>
          <IonButton
            fill='clear'
            color='dark'
            className={classes.filterButton}
            size='small'
            onClick={() => setShowFilters((prevValue) => !prevValue)}
          >
            <IonIcon slot='icon-only' icon={filter}></IonIcon>
          </IonButton>
        </IonToolbar>
        <IonToolbar className={classes.typeFilters}>
          <TypeFilters />
        </IonToolbar>
      </IonHeader>
      <IonContent className={classes.ionContent}>
        {isLoading && !isRefreshing && !isInfiniteScrollLoading
          ? _renderLoader()
          : _renderContent()}
      </IonContent>
    </IonPage>
  );
};

export default observer(EducationalResourcePage);
